// src/components/CottageGoodsList.js
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { updateCottageGoods } from "../actions/cottageActions";
import EditGoodModal from "./EditGoodModal";
import {
  Calculator,
  CheckCircle2,
  ClipboardList,
  Edit3,
  Hash,
  Package,
  Percent,
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v ?? "-";
  return n.toLocaleString("fa-IR");
};

const Cell = ({ children, className }) => (
  <td
    className={cx("px-3 py-3 align-top text-right text-slate-800", className)}
  >
    {children}
  </td>
);

const HeadCell = ({ children, className }) => (
  <th
    className={cx(
      "px-3 py-3 text-right text-xs font-extrabold tracking-tight text-slate-600 whitespace-nowrap",
      className
    )}
  >
    {children}
  </th>
);

const Tag = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-700">
    {Icon ? <Icon size={14} className="text-slate-500" /> : null}
    {label}
  </span>
);

const CottageGoodsList = ({ goods = [] }) => {
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGood, setSelectedGood] = useState(null);

  const rows = useMemo(() => goods || [], [goods]);

  const handleEditClick = (good) => {
    setSelectedGood(good);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGood(null);
  };

  const handleSaveGood = async (updatedGood) => {
    await dispatch(updateCottageGoods(updatedGood));
    handleCloseModal();
  };

  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-right text-sm text-slate-600">
        کالایی یافت نشد.
      </div>
    );
  }

  return (
    <div className="font-vazirmatn" dir="rtl">
      {/* Small summary row (optional) */}
      <div className="flex flex-col sm:flex-row-reverse sm:items-center sm:justify-between gap-2 mb-3">
        <div className="flex flex-row-reverse flex-wrap items-center gap-2">
          <Tag icon={Package} label={`${rows.length} قلم کالا`} />
          <Tag icon={ClipboardList} label="جزئیات هزینه‌ها و مبنا" />
        </div>

        <div className="text-right text-xs text-slate-500">
          برای ویرایش هر ردیف از دکمه «ویرایش» استفاده کنید.
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full border-collapse">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <HeadCell>
                  <span className="inline-flex items-center gap-1.5">
                    <Hash size={14} className="text-slate-500" />
                    ردیف
                  </span>
                </HeadCell>

                <HeadCell>مبنای محاسبه</HeadCell>
                <HeadCell>HS Code</HeadCell>
                <HeadCell>حقوق ورودی</HeadCell>
                <HeadCell>ارزش کل ارزی</HeadCell>
                <HeadCell>ارزش افزوده</HeadCell>

                <HeadCell>
                  <span className="inline-flex items-center gap-1.5">
                    <Percent size={14} className="text-slate-500" />
                    حلال احمر
                  </span>
                </HeadCell>

                <HeadCell>حواله ریالی</HeadCell>
                <HeadCell>حواله + حقوق</HeadCell>
                <HeadCell>سایر هزینه‌ها</HeadCell>

                <HeadCell>
                  <span className="inline-flex items-center gap-1.5">
                    <Calculator size={14} className="text-slate-500" />
                    بهای تمام‌شده
                  </span>
                </HeadCell>

                <HeadCell>بهای هر قلم</HeadCell>

                <HeadCell>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-slate-500" />
                    تعداد
                  </span>
                </HeadCell>

                <HeadCell>
                  <span className="inline-flex items-center gap-1.5">
                    <Package size={14} className="text-slate-500" />
                    شرح کالا
                  </span>
                </HeadCell>

                <HeadCell className="sticky left-0 bg-slate-50">
                  عملیات
                </HeadCell>
              </tr>
            </thead>

            <tbody>
              {rows.map((good, idx) => {
                const qty = Number(good.quantity) || 0;
                const finalPrice = Number(good.final_price) || 0;
                const perItem = qty > 0 ? finalPrice / qty : null;

                return (
                  <tr
                    key={good.id ?? idx}
                    className={cx(
                      "border-b border-slate-100 last:border-b-0",
                      "hover:bg-slate-50/60 transition-colors"
                    )}
                  >
                    <Cell className="text-slate-500 font-bold whitespace-nowrap">
                      {idx + 1}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.customs_value)}
                    </Cell>

                    <Cell className="whitespace-nowrap font-semibold">
                      {good.HSCode || "-"}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.import_rights)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.total_value)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.added_value)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.red_cersent)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.riali)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.hhhg)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(good.other_expense)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {fmtNum(finalPrice)}
                    </Cell>

                    <Cell className="whitespace-nowrap">
                      {perItem != null ? fmtNum(perItem) : "-"}
                    </Cell>

                    <Cell className="whitespace-nowrap">{fmtNum(qty)}</Cell>

                    <Cell className="min-w-[260px]">
                      <div className="text-sm font-semibold text-slate-800">
                        {good.goods_description || "-"}
                      </div>
                    </Cell>

                    <Cell className="sticky left-0 bg-white">
                      <button
                        type="button"
                        onClick={() => handleEditClick(good)}
                        className={cx(
                          "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold",
                          "bg-primary text-white hover:opacity-95 active:opacity-90 transition shadow-sm"
                        )}
                      >
                        <Edit3 size={16} />
                        ویرایش
                      </button>
                    </Cell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedGood ? (
        <EditGoodModal
          good={selectedGood}
          onClose={handleCloseModal}
          onSave={handleSaveGood}
        />
      ) : null}
    </div>
  );
};

export default CottageGoodsList;
